import { createProject, type AssetReference, type PhotobookProject } from '@fotoboek/domain';
import { assetReferenceSchema } from '@fotoboek/domain';
import { applyTemplateToPage, instantiateTemplate } from '@fotoboek/templates';
import { assetBlobStore } from '../storage/blobStore';
import type { AssetRuntime } from '../state/projectStore';

/**
 * Builds a fully local demo project so the app can be tried out without any
 * network access, real photos, or an account: every "photo" is a small
 * generated gradient image, rendered on a `<canvas>` and written straight to
 * OPFS/IndexedDB through the same `assetBlobStore` real imports use.
 */

const GRADIENT_PAIRS: Array<[string, string]> = [
  ['#f6d365', '#fda085'],
  ['#84fab0', '#8fd3f4'],
  ['#a1c4fd', '#c2e9fb'],
  ['#fbc2eb', '#a6c1ee'],
  ['#fddb92', '#d1fdff'],
  ['#96fbc4', '#f9f586'],
  ['#f6e6ca', '#dbaacb'],
  ['#c1dfc4', '#deecdd'],
];

/** Page templates cycled across the demo book's spreads for visual variety. */
const DEMO_PAGE_TEMPLATE_IDS = [
  'page-full-bleed',
  'page-two-horizontal',
  'page-hero-two-small',
  'page-photo-caption',
  'page-four-grid',
  'page-mosaic',
  'page-two-vertical',
  'page-quiet-text',
];

async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function drawGradient(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  colors: [string, string],
): void {
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, colors[0]);
  gradient.addColorStop(1, colors[1]);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality = 0.88): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Kan geen demoafbeelding genereren.'))),
      'image/jpeg',
      quality,
    );
  });
}

function makeGradientCanvas(
  width: number,
  height: number,
  colors: [string, string],
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Kan geen 2D-canvascontext aanmaken.');
  drawGradient(ctx, width, height, colors);
  return canvas;
}

type DemoAsset = { asset: AssetReference; runtime: AssetRuntime };

async function createDemoAsset(projectId: string, index: number): Promise<DemoAsset> {
  const colors = GRADIENT_PAIRS[index % GRADIENT_PAIRS.length]!;
  const width = 1600;
  const height = 1200;
  const originalCanvas = makeGradientCanvas(width, height, colors);
  const thumbnailCanvas = makeGradientCanvas(480, 360, colors);
  const [originalBlob, thumbnailBlob] = await Promise.all([
    canvasToJpegBlob(originalCanvas),
    canvasToJpegBlob(thumbnailCanvas),
  ]);

  const assetId = crypto.randomUUID();
  await Promise.all([
    assetBlobStore.put(projectId, assetId, 'original', originalBlob),
    assetBlobStore.put(projectId, assetId, 'thumbnail', thumbnailBlob),
  ]);

  const checksum = await sha256Hex(await originalBlob.arrayBuffer());
  const asset = assetReferenceSchema.parse({
    id: assetId,
    originalFileName: `demo-${index + 1}.jpg`,
    mimeType: 'image/jpeg',
    byteSize: originalBlob.size,
    widthPx: width,
    heightPx: height,
    exifOrientation: 1,
    importedAt: new Date().toISOString(),
    checksumSha256: checksum,
    syncStatus: 'local-only',
    useCount: 0,
  });

  return {
    asset,
    runtime: {
      objectUrl: URL.createObjectURL(originalBlob),
      thumbnailUrl: URL.createObjectURL(thumbnailBlob),
      widthPx: width,
      heightPx: height,
    },
  };
}

const DEMO_ASSET_COUNT = 8;
const DEMO_FILLED_SPREADS = DEMO_PAGE_TEMPLATE_IDS.length;

export type DemoProjectResult = {
  project: PhotobookProject;
  assetsRuntime: Record<string, AssetRuntime>;
};

/**
 * Creates a demo project entirely from generated images — no external
 * network requests, no bundled sample photos. Safe to call repeatedly; each
 * call mints a fresh project id and asset ids.
 */
export async function createDemoProject(): Promise<DemoProjectResult> {
  let project = createProject({
    title: 'Demo fotoboek',
    description:
      'Voorbeeldproject met automatisch gegenereerde afbeeldingen — geen internetverbinding nodig.',
    ownerMode: 'local',
  });

  const demoAssets: DemoAsset[] = [];
  for (let i = 0; i < DEMO_ASSET_COUNT; i++) {
    demoAssets.push(await createDemoAsset(project.id, i));
  }
  let assetCursor = 0;
  const nextAssetId = () => {
    const picked = demoAssets[assetCursor % demoAssets.length]!;
    assetCursor += 1;
    return picked.asset.id;
  };

  const pages = project.pages.map((page, index) => {
    if (index >= DEMO_FILLED_SPREADS) return page;
    const templateId = DEMO_PAGE_TEMPLATE_IDS[index % DEMO_PAGE_TEMPLATE_IDS.length]!;
    const applied = applyTemplateToPage(page, templateId, { redistributePhotos: false });
    return {
      ...applied,
      elements: applied.elements.map((el) =>
        el.type === 'photoFrame' ? { ...el, assetId: nextAssetId() } : el,
      ),
    };
  });

  const coverFront = instantiateTemplate('cover-front-hero');
  const coverFrontElements = coverFront.elements.map((el) => {
    if (el.type === 'photoFrame') return { ...el, assetId: demoAssets[0]!.asset.id };
    if (el.type === 'text') return { ...el, content: project.title };
    return el;
  });
  const coverBack = instantiateTemplate('cover-back-simple');

  project = {
    ...project,
    pages,
    assets: demoAssets.map((d) => d.asset),
    cover: {
      ...project.cover,
      panels: {
        ...project.cover.panels,
        front: {
          ...project.cover.panels.front,
          background: coverFront.background,
          elements: coverFrontElements,
        },
        back: {
          ...project.cover.panels.back,
          background: coverBack.background,
          elements: coverBack.elements,
        },
      },
    },
  };

  const assetsRuntime = Object.fromEntries(demoAssets.map((d) => [d.asset.id, d.runtime]));

  return { project, assetsRuntime };
}
